import { useId, useState } from "react"
import { FiChevronDown } from "react-icons/fi"
import { useFilingSidebarPanel } from "./FilingSidebarAccordion"

type ServiceType = "trademark" | "design" | "patent-direct" | "patent-pct"

const services: Record<ServiceType, { title: string; sections: { title: string; items: string[] }[] }> = {
  trademark: {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Basic fee for representation of an EU trademark application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Union Intellectual Property Office (EUIPO) for one class, excluding preparation of the list of goods and services.",
          "Preparation of Power of Attorney",
          "Report on submitted order",
          "Filing of the trademark application",
          "Payment of official fees after receipt of payment.",
          "Determination and monitoring of deadlines for the EU trademark application arising by filing.",
          "Filing report",
          "Forwarding of official communications without commentary.",
          "Two reminders regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines."
        ]
      },
      {
        title: "Optional services and additional classes",
        items: [
          "Claiming priority, including procurement and submission of a priority document.",
          "Additional fee for a second class, excluding preparation of the list of goods and services.",
          "Additional fee for each further class, excluding preparation of the list of goods and services."
        ]
      }
    ]
  },
  design: {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Basic fee for representation of an EU design application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Union Intellectual Property Office (EUIPO) for one design",
          "Preparation of Power of Attorney",
          "Report on submitted order",
          "Filing of the design application",
          "Payment of official fees after receipt of payment.",
          "Determination and monitoring of deadlines for the EU design application arising by filing.",
          "Filing report",
          "Forwarding of official communications without commentary.",
          "Two reminders regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines.",
          "Claiming priority, including procurement and submission of a priority document."
        ]
      },
      {
        title: "Optional services and additional designs",
        items: [
          "Additional fee for each additional design in the same application, for designs 2–10.",
          "Additional fee for each additional design in the same application over ten."
        ]
      }
    ]
  },
  "patent-direct": {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Basic fee for representation of an EP patent application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Patent Office (EPO).",
          "Preparation of Power of Attorney",
          "Report on submitted order",
          "Filing of the patent application",
          "Payment of official fees after receipt of payment.",
          "Determination and monitoring of deadlines for the EP patent application arising by filing.",
          "Filing report",
          "Forwarding of official communications only without commentary.",
          "Two reminders regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines.",
          "Claiming priority, including procurement and submission of a priority document."
        ]
      }
    ]
  },
  "patent-pct": {
    title: "Our services",
    sections: [
      {
        title: "Basic fee",
        items: [
          "Basic fee for representation of an EP patent application.",
          "Drafting, compilation, and filing of the application and necessary filing documents with the European Patent Office (EPO).",
          "Preparation of Power of Attorney",
          "Report on submitted order",
          "Filing of the patent application",
          "Payment of official fees after receipt of payment.",
          "Determination and monitoring of deadlines for the EP patent application arising by filing.",
          "Filing report",
          "Forwarding of official communications only without commentary.",
          "Two reminders regarding the invoice payment deadline, a prerequisite for payment of official fees.",
          "No monitoring of maintenance or renewal deadlines.",
          "Claiming priority, including procurement and submission of a priority document."
        ]
      }
    ]
  }
}

export default function ServiceDescriptions({
  type
}: {
  type: ServiceType
}) {
  const [open, toggleOpen] = useFilingSidebarPanel("services")
  const service = services[type]
  const sections = (
    <div className="divide-y divide-gray-200 border-t border-gray-200 text-xs leading-relaxed text-gray-700">
      {service.sections.map((section) => (
        <ServiceSection key={`${type}-${section.title}`} section={section} />
      ))}
    </div>
  )

  return (
    <div className="mt-3 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-sm">
      <button
        type="button"
        className="w-full cursor-pointer bg-gray-50 px-4 py-3 text-left font-medium text-dantzBlue"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <span className="flex items-center justify-between gap-3">
          {service.title}
          <FiChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open && sections}
    </div>
  )
}

function ServiceSection({ section }: { section: { title: string; items: string[] } }) {
  const [open, setOpen] = useState(false)
  const contentId = useId()

  return (
    <section className="grid grid-cols-1 content-start" style={{ rowGap: open ? 8 : 0 }}>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-dantzBlue"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(current => !current)}
      >
        {section.title}
        <FiChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <div id={contentId} hidden={!open} className="px-4 pb-4">
        <ul className="m-0 list-disc space-y-1.5 pl-4">
          {section.items.map(item => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </section>
  )
}
